import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Info } from 'lucide-react';
import '../styles/tailwind.css';

interface CompanyData {
  companyName: string;
  domain: string;
  useCustomId: boolean;
  useDashzapSubdomain: boolean;
}

interface User {
  userId: string;
  email: string;
  name: string;
  companyId: string;
  role: string;
}

interface OnboardingState {
  step: number;
  user: User | null;
  companyData: CompanyData;
  isLoading: boolean;
  error: string;
}

const OnboardingFlow: React.FC = () => {
  const navigate = useNavigate();

  const [state, setState] = useState<OnboardingState>({
    step: 1,
    user: null,
    companyData: {
      companyName: '',
      domain: '',
      useCustomId: false,
      useDashzapSubdomain: true
    },
    isLoading: false,
    error: ''
  });

  const handleCompanyDataChange = (field: keyof CompanyData, value: string | boolean) => {
    setState(prev => ({
      ...prev,
      companyData: {
        ...prev.companyData,
        [field]: value
      }
    }));
  };

  const handleNextStep = () => {
    if (validateCurrentStep()) {
      setState(prev => ({ ...prev, step: 2 }));
    }
  };

  const handlePrevStep = () => {
    setState(prev => ({ ...prev, step: 1 }));
  };

  const validateCurrentStep = (): boolean => {
    const { companyData } = state;

    if (!companyData.companyName) {
      setState(prev => ({ ...prev, error: 'Company name is required' }));
      return false;
    }

    if (!companyData.domain) {
      setState(prev => ({ ...prev, error: 'Company domain is required' }));
      return false;
    }

    if (companyData.useCustomId) {
      const customIdRegex = /^[a-zA-Z0-9][a-zA-Z0-9_-]*[a-zA-Z0-9]$/;
      if (companyData.domain.length < 3) {
        setState(prev => ({ ...prev, error: 'Company identifier must be at least 3 characters' }));
        return false;
      }
      if (!customIdRegex.test(companyData.domain)) {
        setState(prev => ({ ...prev, error: 'Company identifier can only contain letters, numbers, hyphens, and underscores' }));
        return false;
      }
    } else {
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$/;
      if (!domainRegex.test(companyData.domain)) {
        setState(prev => ({ ...prev, error: 'Please enter a valid domain (e.g., company.com)' }));
        return false;
      }
    }

    setState(prev => ({ ...prev, error: '' }));
    return true;
  };

  const handleGoogleLogin = async () => {
    setState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const response = await fetch(`${config.API_BASE_URL}/auth/initiate/onboarding`, {
        method: 'GET',
      });

      const data = await response.json();

      if (response.ok && data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        setState(prev => ({
          ...prev,
          error: data.message || 'Failed to initiate authentication',
          isLoading: false
        }));
      }
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: 'Network error',
        isLoading: false
      }));
    }
  };

  const createCompany = async () => {
    setState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const token = localStorage.getItem('dashzap_token');
      if (!token) {
        setState(prev => ({ ...prev, error: 'Authentication required', isLoading: false }));
        return;
      }

      const companyPayload = {
        companyName: state.companyData.companyName,
        domain: state.companyData.domain,
        allowedEmails: [],
        useCustomId: state.companyData.useCustomId,
        useDashzapSubdomain: state.companyData.useDashzapSubdomain
      };

      const response = await fetch(`${config.API_BASE_URL}/admin/add-company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(companyPayload),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('dashzap_token', data.token);
        navigate('/admin-dashboard');
      } else {
        setState(prev => ({
          ...prev,
          error: data.error || 'Failed to create company',
          isLoading: false
        }));
      }
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: 'Network error',
        isLoading: false
      }));
    }
  };

  useEffect(() => {
    // Check if we're returning from Google OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const tempToken = urlParams.get('token');
    const company = urlParams.get('company');

    if (tempToken && company === 'onboarding') {
      try {
        const payload = JSON.parse(atob(tempToken.split('.')[1]));
        localStorage.setItem('dashzap_token', tempToken);

        setState(prev => ({
          ...prev,
          user: {
            userId: '',
            email: payload.email,
            name: payload.name,
            companyId: 'onboarding',
            role: 'admin'
          },
          step: 1, // Start at step 1 (company info) after successful login
        }));
      } catch (error) {
        console.error('Error processing OAuth callback:', error);
      }
    } else {
      // Check if user is already authenticated
      const token = localStorage.getItem('dashzap_token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.companyId === 'onboarding') {
            // User is authenticated but hasn't completed onboarding
            setState(prev => ({
              ...prev,
              user: {
                userId: '',
                email: payload.email,
                name: payload.name,
                companyId: 'onboarding',
                role: 'admin'
              },
              step: 1, // Show company info form
            }));
          } else {
            // User already has a company, redirect to dashboard
            navigate('/admin-dashboard');
          }
        } catch (error) {
          console.error('Error decoding token:', error);
          // Invalid token, show login (step 0)
          setState(prev => ({ ...prev, step: 0 }));
        }
      } else {
        // No authentication, show Google login screen (step 0)
        setState(prev => ({ ...prev, step: 0 }));
      }
    }
  }, [navigate]);

  const renderStep = () => {
    switch (state.step) {
      case 0:
        // Google Login Screen (shown when not authenticated)
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-3xl font-semibold text-gray-900">Admin / Company Onboarding</h2>
              <p className="text-gray-600 mt-3 text-lg">Sign in with Google to get started</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <p className="text-blue-900 font-medium mb-2">📋 What you'll set up:</p>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>✓ Create your company account</li>
                <li>✓ Become the admin of your company</li>
                <li>✓ Manage users and services</li>
              </ul>
            </div>

            <div className="flex flex-col items-center space-y-4 py-6">
              <div className="flex items-center space-x-2 text-gray-600 text-sm">
                <Info className="w-5 h-5" />
                <span>Sign in as an Admin to continue</span>
              </div>

              <Button
                onClick={handleGoogleLogin}
                disabled={state.isLoading}
                size="lg"
                className="w-full max-w-md h-14 text-base"
              >
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  className="w-5 h-5 mr-3"
                />
                {state.isLoading ? 'Signing in...' : 'Sign in via Google as Admin'}
              </Button>
            </div>

            <div className="text-center text-sm text-gray-500 mt-4">
              <p>By signing in, you agree to become the admin of your company</p>
              <p className="mt-1">You'll be able to add other admins later via User Management</p>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Company Information</h2>
              <p className="text-gray-600 mt-2">Let's start by setting up your company details.</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Company Name *</Label>
                <Input
                  id="company-name"
                  type="text"
                  value={state.companyData.companyName}
                  onChange={(e) => handleCompanyDataChange('companyName', e.target.value)}
                  placeholder="Acme Inc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="domain">
                  {state.companyData.useCustomId ? 'Company Identifier *' : 'Company Domain *'}
                </Label>
                <Input
                  id="domain"
                  type="text"
                  value={state.companyData.domain}
                  onChange={(e) => handleCompanyDataChange('domain', e.target.value.toLowerCase())}
                  placeholder={state.companyData.useCustomId ? 'my-company' : 'acme.com'}
                />
                <p className="text-sm text-gray-500">
                  {state.companyData.useCustomId
                    ? 'Use letters, numbers, hyphens, and underscores (min 3 characters)'
                    : 'This will be used to identify your company and validate user emails'}
                </p>
              </div>

              <div className="space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="use-custom-id"
                    checked={state.companyData.useCustomId}
                    onCheckedChange={(checked: boolean) => handleCompanyDataChange('useCustomId', !!checked)}
                  />
                  <Label htmlFor="use-custom-id" className="font-normal cursor-pointer">
                    Company domain doesn't exist (use custom identifier)
                  </Label>
                </div>
                <p className="text-sm text-gray-500 ml-6">
                  Check this if you don't have a company domain or prefer to use a custom identifier
                </p>
              </div>

              {!state.companyData.useCustomId && (
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="use-subdomain"
                      checked={state.companyData.useDashzapSubdomain}
                      onCheckedChange={(checked: boolean) => handleCompanyDataChange('useDashzapSubdomain', !!checked)}
                    />
                    <Label htmlFor="use-subdomain" className="font-normal cursor-pointer">
                      Reserve <strong>apps.{state.companyData.domain || 'yourdomain.com'}</strong> for Dashzap services
                    </Label>
                  </div>
                  <p className="text-sm text-gray-500 ml-6">
                    Recommended: Automatic SSL, authentication, and admin dashboard at your subdomain
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Review & Complete Setup</h2>
              <div className="mt-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800">✅ Review your company details and admin account, then complete the setup.</p>
                </div>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Admin Account</CardTitle>
              </CardHeader>
              <CardContent>
                {state.user && (
                  <div className="flex items-center space-x-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold">
                      {state.user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{state.user.name}</p>
                      <p className="text-sm text-gray-600">{state.user.email}</p>
                      <p className="text-xs text-indigo-600 mt-1">Company Admin</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Company Name:</span>
                  <span className="text-gray-900">{state.companyData.companyName}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">
                    {state.companyData.useCustomId ? 'Identifier:' : 'Domain:'}
                  </span>
                  <span className="text-gray-900">{state.companyData.domain}</span>
                </div>
                {!state.companyData.useCustomId && state.companyData.useDashzapSubdomain && (
                  <div className="flex justify-between items-center py-2">
                    <span className="font-medium text-gray-700">Dashzap Subdomain:</span>
                    <span className="text-gray-900">apps.{state.companyData.domain}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <h1 className="text-2xl font-bold text-indigo-600">Dashzap</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[
              { num: 0, label: 'Sign In' },
              { num: 1, label: 'Company Info' },
              { num: 2, label: 'Review & Complete' }
            ].map((step, index) => (
              <React.Fragment key={step.num}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      step.num < state.step
                        ? 'bg-green-500 text-white'
                        : step.num === state.step
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step.num < state.step ? '✓' : step.num + 1}
                  </div>
                  <span className={`text-sm mt-2 font-medium ${
                    step.num <= state.step ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {step.label}
                  </span>
                </div>
                {index < 2 && (
                  <div className={`w-16 h-1 rounded ${
                    step.num < state.step ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <Card className="shadow-lg">
          <CardContent className="p-8">
            {state.error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">{state.error}</p>
              </div>
            )}

            {renderStep()}

            {state.step > 0 && (
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                {state.step > 1 ? (
                  <Button
                    onClick={handlePrevStep}
                    variant="outline"
                    disabled={state.isLoading}
                  >
                    Previous
                  </Button>
                ) : <div />}

                {state.step === 1 && (
                  <Button
                    onClick={handleNextStep}
                    disabled={state.isLoading}
                    className="ml-auto"
                  >
                    Next
                  </Button>
                )}

                {state.step === 2 && state.user && (
                  <Button
                    onClick={createCompany}
                    disabled={state.isLoading}
                    className="ml-auto"
                  >
                    {state.isLoading ? 'Creating Company...' : 'Complete Setup'}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingFlow;
